import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Tooltip,
  Snackbar,
  Checkbox,
} from '@mui/material';
import SuperAdminService from '../../services/SuperAdminService';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SuperAdminLayout from './SuperAdminLayout';

interface UserInfo {
  firstName: string;
  lastName: string;
  email: string;
  photoURL: string;
  companyId: string | null;
  role: string;
}

interface DeletionRequest {
  id: string;
  userId: string;
  email: string;
  requestDate: string;
  timestamp: number;
  status: 'pending' | 'completed';
  userInfo: UserInfo;
}

const DeletionRequests: React.FC = () => {
  const [requests, setRequests] = useState<DeletionRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentUser, userDetails } = useAuth();
  const navigate = useNavigate();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const handleCopyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setSnackbarMessage(`${label} kopyalandı!`);
      setSnackbarOpen(true);
    }).catch((err) => {
      console.error('Kopyalama hatası:', err);
      setSnackbarMessage('Kopyalama başarısız!');
      setSnackbarOpen(true);
    });
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleMarkCompleted = async (requestId: string, currentStatus: string) => {
    if (currentStatus === 'completed') {
      setSnackbarMessage('Bu talep zaten tamamlanmış!');
      setSnackbarOpen(true);
      return;
    }

    try {
      const superAdminService = SuperAdminService.getInstance();
      const result = await superAdminService.markDeletionRequestCompleted(requestId);
      
      if (result.success) {
        // Listeyi yeniden yükle
        const updatedRequests = await superAdminService.getDeletionRequests();
        if (updatedRequests.success) {
          setRequests(updatedRequests.requests || []);
        }
        
        setSnackbarMessage('Silme talebi tamamlandı olarak işaretlendi!');
        setSnackbarOpen(true);
      } else {
        throw new Error(result.message || 'İşaretleme başarısız');
      }
    } catch (err: any) {
      console.error('Silme talebi işaretlenirken hata:', err);
      setSnackbarMessage(err.message || 'Bir hata oluştu');
      setSnackbarOpen(true);
    }
  };

  useEffect(() => {
    // SuperAdmin yetkisi kontrolü
    if (!userDetails || userDetails.role !== 'superadmin') {
      navigate('/login');
      return;
    }

    const fetchDeletionRequests = async () => {
      try {
        setLoading(true);
        const superAdminService = SuperAdminService.getInstance();
        const result = await superAdminService.getDeletionRequests();
        
        if (result.success) {
          setRequests(result.requests || []);
        } else {
          throw new Error(result.message || 'Silme talepleri alınamadı');
        }
      } catch (err: any) {
        console.error('Silme talepleri yüklenirken hata:', err);
        setError(err.message || 'Bir hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    fetchDeletionRequests();
  }, [currentUser, userDetails, navigate]);

  return (
    <SuperAdminLayout title="Hesap Silme Talepleri" icon={<DeleteSweepIcon />}>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      ) : requests.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          Bekleyen hesap silme talebi bulunmuyor.
        </Alert>
      ) : (
        <TableContainer component={Paper} sx={{ mt: 2, overflowX: 'auto' }}>
          <Table sx={{ minWidth: 900 }}>
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell>Kullanıcı</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Rol</TableCell>
                <TableCell>Kullanıcı ID</TableCell>
                <TableCell>Şirket ID</TableCell>
                <TableCell>Talep Tarihi</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((request) => (
                <TableRow key={request.id} sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar src={request.userInfo.photoURL} alt={`${request.userInfo.firstName} ${request.userInfo.lastName}`}>
                        {(request.userInfo.firstName?.charAt(0) || request.email.charAt(0)).toUpperCase()}
                      </Avatar>
                      <Typography variant="body1">
                        {`${request.userInfo.firstName} ${request.userInfo.lastName}`}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{request.userInfo.email}</TableCell>
                  <TableCell>
                    <Box 
                      component="span"
                      sx={{
                        bgcolor: request.userInfo.role === 'admin' ? '#d32f2f' : 
                                 request.userInfo.role === 'employee' ? '#1976d2' : 
                                 request.userInfo.role === 'superadmin' ? '#2e7d32' : 
                                 'grey.300',
                        color: request.userInfo.role === 'admin' ? 'white' : 
                               request.userInfo.role === 'employee' ? 'white' : 
                               request.userInfo.role === 'superadmin' ? 'white' : 
                               'grey.700',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '12px',
                        fontWeight: 'medium',
                        fontSize: '0.7rem',
                        textTransform: 'capitalize',
                      }}
                    >
                      {request.userInfo.role === 'admin' ? 'Yönetici' :
                       request.userInfo.role === 'employee' ? 'Personel' :
                       request.userInfo.role === 'superadmin' ? 'Süper Admin' :
                       request.userInfo.role}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontFamily: 'monospace', 
                          fontSize: '0.75rem',
                          bgcolor: 'rgba(0,0,0,0.05)',
                          p: 0.5,
                          borderRadius: 1,
                          display: 'inline-block'
                        }}
                      >
                        {request.userId}
                      </Typography>
                      <Tooltip title="Kopyala">
                        <IconButton 
                          size="small" 
                          onClick={() => handleCopyToClipboard(request.userId, 'Kullanıcı ID')}
                          sx={{ 
                            p: 0.5,
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' }
                          }}
                        >
                          <ContentCopyIcon sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {request.userInfo.companyId ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontFamily: 'monospace', 
                            fontSize: '0.75rem',
                            bgcolor: 'rgba(25, 118, 210, 0.1)',
                            color: 'primary.main',
                            p: 0.5,
                            borderRadius: 1,
                            display: 'inline-block'
                          }}
                        >
                          {request.userInfo.companyId}
                        </Typography>
                        <Tooltip title="Kopyala">
                          <IconButton 
                            size="small" 
                            onClick={() => handleCopyToClipboard(request.userInfo.companyId!, 'Şirket ID')}
                            sx={{ 
                              p: 0.5,
                              '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' }
                            }}
                          >
                            <ContentCopyIcon sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'text.secondary',
                          fontStyle: 'italic',
                          fontSize: '0.75rem'
                        }}
                      >
                        Şirket Yok
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Tooltip title={new Date(request.timestamp).toLocaleString('tr-TR')}>
                      <Typography variant="body2">{request.requestDate}</Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Box 
                      component="span"
                      sx={{
                        bgcolor: request.status === 'pending' ? 'warning.light' : 'success.light',
                        color: request.status === 'pending' ? 'warning.dark' : 'success.dark',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '12px',
                        fontWeight: 'medium',
                        fontSize: '0.75rem',
                      }}
                    >
                      {request.status === 'pending' ? 'Bekliyor' : 'Tamamlandı'}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Checkbox
                      checked={request.status === 'completed'}
                      onChange={() => handleMarkCompleted(request.id, request.status)}
                      disabled={request.status === 'completed'}
                      sx={{
                        color: 'success.main',
                        '&.Mui-checked': {
                          color: 'success.main',
                        },
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </SuperAdminLayout>
  );
};

export default DeletionRequests;
